import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuditorCompany, AuditorProfile } from '../types/auditor';
import { useAuthStore } from './authStore';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { ContextService } from '../services/ContextService';
import { ChartsApiService } from '../services/chartsApi';
import { clearApiRequestCache } from '../services/api/ApiService';
import { getQueryClient } from '../hooks/queries/queryConfig';
import { ChatStreamService } from '../services/api/streaming/ChatStreamService';

/**
 * Purge toutes les données locales spécifiques à une entreprise.
 * Appelé UNIQUEMENT quand un auditeur change d'entreprise active.
 * Les utilisateurs normaux ne passent jamais par ce chemin.
 */
async function clearCompanyData(): Promise<void> {
  console.log('🧹 [Auditor] Purge des données company précédente...');

  // 1. Caches mémoire (synchrones, rapides)
  ContextService.invalidateCache();
  ChartsApiService.clearCache();
  clearApiRequestCache();

  // 2. React Query cache
  const qc = getQueryClient();
  if (qc) qc.clear();

  // 3. Déconnecter le chat streaming (la reconnexion utilisera le nouveau companyId)
  ChatStreamService.disconnect();

  // 4. IndexedDB — vider les stores contenant des données financières/company
  const companyStores = [
    'journal_entries',
    'accounts',
    'fiscal_years',
    'analytics',
    'declarations',
    'audit_history',
    'sync_queue',
    'chat_history',
    'dashboard_data',
    'dashboard_timeseries',
    'organization',
    'generated_reports',
    'activities',
    'treasury_accounts',
    'agent_entries',
    'business_units',
  ];
  await Promise.allSettled(
    companyStores.map((store) => IndexedDBService.clear(store))
  );

  // 5. localStorage — clés spécifiques company
  const companyLocalStorageKeys = [
    'fiscal-year-storage',
    'business-unit-storage',
    'accounting-level-settings',
    'journal-entries',
    'wanzo_organization',
    'app-settings',
  ];
  companyLocalStorageKeys.forEach((key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  });

  // 6. Zustand stores company-specific — reset via setState
  // (imports circulaires évités car on accède aux stores via .getState() au runtime)
  try {
    const { useFiscalYearStore } = await import('./fiscalYearStore');
    useFiscalYearStore.setState({ currentFiscalYear: null });
  } catch { /* ignore if store not loaded */ }

  try {
    const { useDashboardStore } = await import('./dashboardStore');
    useDashboardStore.getState().resetLayout();
  } catch { /* ignore */ }

  try {
    const { useBusinessUnitStore } = await import('./businessUnitStore');
    useBusinessUnitStore.getState().resetToCompany();
  } catch { /* ignore */ }

  // 7. Chat store — reset conversations
  try {
    const { useChatStore } = await import('../hooks/useChatStore');
    useChatStore.setState({
      conversations: [],
      activeConversationId: null,
      lastError: null,
      isStreaming: false,
      streamingContent: '',
      streamingMessageId: null,
    });
  } catch { /* ignore */ }

  console.log('✅ [Auditor] Purge company terminée');
}

interface AuditorState {
  /** Profil complet chargé depuis /auth/me (non persisté) */
  auditorProfile: AuditorProfile | null;
  /** Entreprise actuellement consultée (persistée pour survivre aux reloads) */
  activeCompany: AuditorCompany | null;
  /** IDs des entreprises ouvertes récemment, limitée à 10 */
  recentlyOpenedIds: string[];

  // Actions
  setAuditorProfile: (profile: AuditorProfile | null) => void;
  setActiveCompany: (company: AuditorCompany | null) => void;
  clearActiveCompany: () => void;
  /** Retourne les entreprises triées par "récemment ouvert" */
  getCompaniesSortedByRecent: () => AuditorCompany[];
}

export const useAuditorStore = create<AuditorState>()(
  persist(
    (set, get) => ({
      auditorProfile: null,
      activeCompany: null,
      recentlyOpenedIds: [],

      setAuditorProfile: (profile) => {
        set({ auditorProfile: profile });
        // Tenter de restaurer l'entreprise active depuis le nouveau profil
        const { activeCompany } = get();
        if (activeCompany && profile) {
          const refreshed = profile.companies.find(
            (c) => c.organization.id === activeCompany.organization.id
          );
          set({ activeCompany: refreshed ?? null });
        }
      },

      setActiveCompany: (company) => {
        set((state) => {
          const newRecentIds = company
            ? [
                company.organization.id,
                ...state.recentlyOpenedIds.filter(
                  (id) => id !== company.organization.id
                ),
              ].slice(0, 10)
            : state.recentlyOpenedIds;
          return { activeCompany: company, recentlyOpenedIds: newRecentIds };
        });
        // Synchroniser l'organisation dans authStore pour que le reste de l'app
        // (requêtes API, navigation, etc.) fonctionne avec l'entreprise sélectionnée
        useAuthStore.getState().setOrganization(company?.organization ?? null);
        console.log('🏢 [Auditor] Active company set:', company?.organization.id, company?.organization.name);
      },

      clearActiveCompany: () => {
        set({ activeCompany: null });
        useAuthStore.getState().setOrganization(null);
      },

      getCompaniesSortedByRecent: () => {
        const { auditorProfile, recentlyOpenedIds } = get();
        if (!auditorProfile) return [];
        const companies = [...auditorProfile.companies];
        companies.sort((a, b) => {
          const aIdx = recentlyOpenedIds.indexOf(a.organization.id);
          const bIdx = recentlyOpenedIds.indexOf(b.organization.id);
          if (aIdx === -1 && bIdx === -1) return 0;
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
        return companies;
      },
    }),
    {
      name: 'wanzo-auditor-storage',
      // Persister activeCompany + recentlyOpenedIds pour survivre aux reloads.
      // Le auditorProfile est re-fetché via /auth/me à chaque connexion.
      partialize: (state) => ({
        activeCompany: state.activeCompany,
        recentlyOpenedIds: state.recentlyOpenedIds,
      }),
    }
  )
);

/**
 * Fonction async pour ouvrir une entreprise en tant qu'auditeur.
 * - Si c'est un changement d'entreprise -> purge les données locales AVANT de switcher
 * - Puis set la nouvelle entreprise active (synchrone)
 * Doit être await avant de naviguer.
 */
export async function switchAuditorCompany(company: AuditorCompany): Promise<void> {
  const { activeCompany, setActiveCompany } = useAuditorStore.getState();
  const previousCompanyId = activeCompany?.organization.id;
  const newCompanyId = company.organization.id;

  // Purger les anciennes données AVANT de changer d'entreprise
  if (previousCompanyId && previousCompanyId !== newCompanyId) {
    console.log('🔄 [Auditor] Changement d\'entreprise:', previousCompanyId, '→', newCompanyId);
    await clearCompanyData();
  }

  // Maintenant que tout est purgé, on set la nouvelle entreprise
  setActiveCompany(company);
}
